
export function tieneFechaSalidaEfectiva(fechaSalida?: string | null): boolean {
  if (!fechaSalida) return false;
  const trimmed = fechaSalida.trim();
  if (!trimmed) return false;
  const year = parseInt(trimmed.substring(0, 4), 10);
  return !Number.isNaN(year) && year >= 1901;
}

export function esChequeTerceroAnulado(cheque: {
  senial?: number | null;
  concepto?: string | null;
}): boolean {
  if (cheque.senial === 0) return true;
  const concepto = (cheque.concepto ?? '').toUpperCase();
  return concepto.includes('ANULADO');
}

export type EstadoChequeTercero = 'anulado' | 'conciliado' | 'entregado' | 'en_cartera';

export function chequeTieneSalidaRegistrada(cheque: {
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
}): boolean {
  if (tieneFechaSalidaEfectiva(cheque.fechaSalida)) return true;
  const nroSalida = Number(cheque.nroMovCajaSalida ?? 0);
  if (Number.isFinite(nroSalida) && nroSalida > 0) return true;
  return (cheque.comprobanteSalida ?? '').trim().length > 0;
}

export function resolverEstadoChequeTercero(cheque: {
  senial?: number | null;
  concepto?: string | null;
  conciliado?: number | null;
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
}): EstadoChequeTercero {
  if (esChequeTerceroAnulado(cheque)) return 'anulado';
  if (cheque.conciliado) return 'conciliado';
  if (chequeTieneSalidaRegistrada(cheque)) return 'entregado';
  return 'en_cartera';
}

export function etiquetaEstadoChequeTercero(estado: EstadoChequeTercero): string {
  switch (estado) {
    case 'anulado':
      return 'Anulado';
    case 'conciliado':
      return 'Conciliado';
    case 'entregado':
      return 'Entregado';
    default:
      return 'En cartera';
  }
}

/** Disponible para endoso / traspaso (no entregado, anulado ni conciliado). */
export function esChequeDisponibleEnCartera(cheque: {
  senial?: number | null;
  concepto?: string | null;
  conciliado?: number | null;
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
}): boolean {
  return resolverEstadoChequeTercero(cheque) === 'en_cartera';
}

type ChequeCarteraEstadoInput = {
  senial?: number | null;
  concepto?: string | null;
  conciliado?: number | null;
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
};

/** Filtro client-side del informe: con estado en_cartera, solo los realmente disponibles. */
export function filtrarChequesInformeCartera<T extends ChequeCarteraEstadoInput>(
  list: T[],
  estadoFiltro: string
): T[] {
  if (estadoFiltro.trim() === 'en_cartera') {
    return list.filter(esChequeDisponibleEnCartera);
  }
  return list;
}

/** Excluye cheques ya utilizados (traspaso/OP) y opcionalmente nroCarga en uso en la sesión. */
export function filtrarChequesDisponiblesParaUso<T extends ChequeCarteraEstadoInput & { nroCarga: number }>(
  list: T[],
  excluirNrosCarga?: ReadonlySet<number>
): T[] {
  return list.filter(
    (c) => esChequeDisponibleEnCartera(c) && !(excluirNrosCarga?.has(c.nroCarga) ?? false)
  );
}
